// ABOUTME: Renders structured insights based on type (metric-card, chart, table, summary, etc).
// ABOUTME: Handles all 6 insight types with appropriate visualizations.

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  Info,
  ChevronRight,
  ChevronDown,
  Download,
  Search,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {
  StructuredInsight,
  ChartConfig,
  TableConfig,
  SummaryConfig,
  KeyMetric,
  SmartAction,
  SelectionReasoning,
  BulletPoint,
} from '@/types/sdk';
import { ChartRenderer } from './ChartRenderer';
import { PythonVizRenderer } from './PythonVizRenderer';
import { PythonVizWithSummaryRenderer } from './PythonVizWithSummaryRenderer';

interface InsightRendererProps {
  insight: StructuredInsight;
  onSmartAction?: (action: SmartAction) => void;
  showSmartActions?: boolean;
  minimal?: boolean;
  hideSummary?: boolean; // Hide summary portion for table-summary types (used in reports)
}

export function InsightRenderer({ insight, onSmartAction, showSmartActions = true, minimal = false, hideSummary = false }: InsightRendererProps) {
  const renderContent = () => {
    switch (insight.type) {
      case 'metric-card':
        return <MetricCardInsight metrics={insight.key_metrics || []} />;
      case 'chart':
        return insight.chart ? <ChartInsight chart={insight.chart} /> : null;
      case 'table':
        return insight.table ? <TableInsight table={insight.table} /> : null;
      case 'summary':
        return insight.summary ? <SummaryInsight summary={insight.summary} /> : null;
      case 'table-summary':
        return (
          <div className="space-y-4">
            {insight.table && <TableInsight table={insight.table} />}
            {!hideSummary && insight.summary && <SummaryInsight summary={insight.summary} />}
          </div>
        );
      case 'chart-table':
        return (
          <div className="space-y-4">
            {insight.chart && <ChartInsight chart={insight.chart} />}
            {insight.table && <TableInsight table={insight.table} compact />}
          </div>
        );
      case 'python-viz':
        return insight.python_viz ? (
          <PythonVizRenderer pythonViz={insight.python_viz} />
        ) : null;
      case 'python-viz-with-summary':
        return insight.python_viz && insight.summary ? (
          <PythonVizWithSummaryRenderer
            pythonViz={insight.python_viz}
            summary={insight.summary}
          />
        ) : null;
      default:
        return <div className="text-surface-500">Unknown insight type</div>;
    }
  };

  // Minimal mode: just render the content without wrapper, header, or actions
  if (minimal) {
    return <div className="insight-content-only">{renderContent()}</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="card rounded-sm overflow-hidden group"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-surface-200">
        <h3 className="text-sm font-medium text-surface-800">{insight.title}</h3>
        {(insight.insight_overview || insight.selection_reasoning) && (
          <InsightMetaBadge
            insightOverview={insight.insight_overview}
            reasoning={insight.selection_reasoning}
          />
        )}
      </div>

      {/* Content */}
      <div className="p-4">{renderContent()}</div>

      {/* Smart Actions - only show on most recent insight */}
      {showSmartActions && insight.smart_actions && insight.smart_actions.length > 0 && (
        <div className="px-4 py-3 border-t border-surface-200 bg-surface-50">
          <SmartActionBar actions={insight.smart_actions} onAction={onSmartAction} />
        </div>
      )}
    </motion.div>
  );
}

// =============================================================================
// Insight Meta Badge (Overview only - no confidence/reasoning)
// =============================================================================

function InsightMetaBadge({
  insightOverview,
}: {
  insightOverview?: string;
  reasoning?: SelectionReasoning;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Only show expand button if overview is long
  const hasExpandableContent = insightOverview && insightOverview.length > 150;

  if (!insightOverview) {
    return null;
  }

  return (
    <div className="mt-2 space-y-2">
      {/* Insight overview */}
      <p className={`text-xs text-surface-500 leading-relaxed ${!isExpanded ? 'line-clamp-2' : ''}`}>
        {insightOverview}
      </p>

      {/* Expand button for long overviews */}
      {hasExpandableContent && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-[11px] text-surface-400 hover:text-primary-600 transition-colors cursor-pointer"
        >
          <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          <span>{isExpanded ? 'Show less' : 'Show more'}</span>
        </button>
      )}
    </div>
  );
}

// =============================================================================
// Metric Card Insight
// =============================================================================

function MetricCardInsight({ metrics }: { metrics: KeyMetric[] }) {
  if (metrics.length === 0) return null;

  // Single metric gets full width, multiple metrics get grid
  if (metrics.length === 1) {
    return <MetricCard metric={metrics[0]} large />;
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {metrics.map((metric, index) => (
        <MetricCard key={index} metric={metric} />
      ))}
    </div>
  );
}

function MetricCard({ metric, large = false }: { metric: KeyMetric; large?: boolean }) {
  const sentimentColors = {
    positive: 'text-success',
    negative: 'text-error',
    neutral: 'text-surface-600',
    warning: 'text-warning',
    critical: 'text-error',
  };

  const TrendIcon = metric.change
    ? metric.change.direction === 'up'
      ? TrendingUp
      : metric.change.direction === 'down'
      ? TrendingDown
      : Minus
    : null;

  return (
    <div
      className={`rounded-sm card ${
        large ? 'p-6' : 'p-4'
      }`}
    >
      <div className="flex items-center gap-2 text-xs text-surface-500 uppercase tracking-wider mb-1">
        {metric.sentiment === 'negative' && metric.change?.direction === 'down' && (
          <div className="w-2 h-2 rounded-full bg-error animate-pulse" />
        )}
        {metric.label}
      </div>
      <div className={`font-mono font-semibold ${large ? 'text-3xl' : 'text-xl'} ${sentimentColors[metric.sentiment]}`}>
        {metric.display_value}
      </div>
      {metric.change && TrendIcon && (
        <div className="flex items-center gap-1 mt-2">
          <TrendIcon
            className={`w-4 h-4 ${
              metric.change.sentiment === 'positive'
                ? 'text-success'
                : metric.change.sentiment === 'negative'
                ? 'text-error'
                : 'text-surface-400'
            }`}
          />
          <span
            className={`text-sm ${
              metric.change.sentiment === 'positive'
                ? 'text-success'
                : metric.change.sentiment === 'negative'
                ? 'text-error'
                : 'text-surface-400'
            }`}
          >
            {metric.change.value > 0 ? '+' : ''}
            {metric.change.value}%
          </span>
          {metric.change.period_label && (
            <span className="text-xs text-surface-400 ml-1">
              {metric.change.period_label}
            </span>
          )}
        </div>
      )}
      {metric.context && (
        <div className="text-xs text-surface-500 mt-2">{metric.context}</div>
      )}
      {metric.sentiment_reason && (
        <div className="flex items-start gap-1 mt-2 text-xs text-surface-500">
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>{metric.sentiment_reason}</span>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Chart Insight
// =============================================================================

function ChartInsight({ chart }: { chart: ChartConfig }) {
  // Detect whether data is multi-series (multiple numeric fields, no standard label/value)
  const firstItem = chart.data[0];
  let isMultiSeries = false;

  if (firstItem) {
    const keys = Object.keys(firstItem);
    const numericKeys = keys.filter(k => typeof firstItem[k] === 'number');
    const hasStandardKeys = 'label' in firstItem && 'value' in firstItem;
    // Multi-series: more than one numeric field and no standard label/value pair
    isMultiSeries = numericKeys.length > 1 && !hasStandardKeys;
  }

  // Map chart type
  const chartType = chart.type === 'bar' || chart.type === 'line' || chart.type === 'area'
    ? chart.type
    : 'bar'; // Default to bar for unsupported types

  if (isMultiSeries) {
    // Pass raw data directly for multi-series rendering
    return (
      <div className="card rounded-sm p-4">
        <ChartRenderer
          rawData={chart.data}
          type={chartType}
          height={220}
        />
        {chart.x_axis?.label && (
          <div className="text-xs text-surface-500 text-center mt-2">
            {chart.x_axis.label}
          </div>
        )}
      </div>
    );
  }

  // Single-series: detect label/value keys and normalize
  let labelKey = 'label';
  let valueKey = 'value';

  if (firstItem) {
    const keys = Object.keys(firstItem);

    // If 'label' doesn't exist, find the first string field
    if (!('label' in firstItem)) {
      const stringKey = keys.find(k => typeof firstItem[k] === 'string');
      if (stringKey) labelKey = stringKey;
    }

    // If 'value' doesn't exist, find the first numeric field
    if (!('value' in firstItem)) {
      const numericKey = keys.find(k => typeof firstItem[k] === 'number');
      if (numericKey) valueKey = numericKey;
    }
  }

  // Convert chart data to format expected by ChartRenderer
  const chartData = chart.data.map((d, index) => {
    return {
      ...d,
      label: d[labelKey] !== undefined ? String(d[labelKey]) : `Item ${index + 1}`,
      value: d[valueKey] !== undefined ? Number(d[valueKey]) : 0,
    };
  });

  return (
    <div className="card rounded-sm p-4">
      <ChartRenderer
        data={chartData}
        type={chartType}
        height={220}
        xAxisKey="label"
        yAxisKey="value"
      />
      {chart.x_axis?.label && (
        <div className="text-xs text-surface-500 text-center mt-2">
          {chart.x_axis.label}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Table Insight
// =============================================================================

function TableInsight({ table, compact = false }: { table: TableConfig; compact?: boolean }) {
  const displayRows = compact
    ? table.rows.slice(0, 5)
    : table.rows.slice(0, table.max_display_rows || 10);
  const hasMore = table.rows.length > displayRows.length;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-surface-200">
            {table.show_row_numbers && (
              <th className="px-3 py-2 text-left text-xs text-surface-500 font-medium">
                #
              </th>
            )}
            {table.columns.map((col) => (
              <th
                key={col.id}
                className={`px-3 py-2 text-xs text-surface-500 font-medium ${
                  col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                }`}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row, rowIndex) => (
            <tr key={rowIndex} className="border-b border-surface-100 last:border-0">
              {table.show_row_numbers && (
                <td className="px-3 py-2 text-surface-400">{rowIndex + 1}</td>
              )}
              {table.columns.map((col) => (
                <td
                  key={col.id}
                  className={`px-3 py-2 text-surface-600 ${
                    col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                  }`}
                >
                  {formatCellValue(row[col.id], col.type)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {hasMore && (
        <div className="text-xs text-surface-400 text-center mt-2 py-2 border-t border-surface-100">
          Showing {displayRows.length} of {table.rows.length} rows
        </div>
      )}
    </div>
  );
}

function formatCellValue(value: unknown, type?: string): string {
  if (value === null || value === undefined) return '-';

  switch (type) {
    case 'currency':
      return typeof value === 'number'
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
        : String(value);
    case 'percentage':
      return typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : String(value);
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : String(value);
    case 'date':
      return value instanceof Date ? value.toLocaleDateString() : String(value);
    default:
      return String(value);
  }
}

// =============================================================================
// Summary Insight
// =============================================================================

function SummaryInsight({ summary }: { summary: SummaryConfig }) {
  return (
    <div className="space-y-3">
      {summary.introduction && (
        <div className="text-sm text-surface-600 leading-relaxed prose prose-sm max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
              strong: ({ children }) => <strong className="text-surface-800 font-semibold">{children}</strong>,
              em: ({ children }) => <em className="text-surface-600">{children}</em>,
              code: ({ children }) => (
                <code className="px-1 py-0.5 rounded bg-surface-100 text-primary-600 font-mono text-xs">
                  {children}
                </code>
              ),
            }}
          >
            {summary.introduction}
          </ReactMarkdown>
        </div>
      )}

      {summary.bullet_points.length > 0 && (
        <ul className="space-y-2">
          {summary.bullet_points.map((point, index) => (
            <BulletPointItem key={index} point={point} />
          ))}
        </ul>
      )}

      {summary.conclusion && (
        <div className="text-sm text-surface-600 leading-relaxed pt-2 border-t border-surface-100 prose prose-sm max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
              strong: ({ children }) => <strong className="text-surface-800 font-semibold">{children}</strong>,
              em: ({ children }) => <em className="text-surface-600">{children}</em>,
              code: ({ children }) => (
                <code className="px-1 py-0.5 rounded bg-surface-100 text-primary-600 font-mono text-xs">
                  {children}
                </code>
              ),
            }}
          >
            {summary.conclusion}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
}

function BulletPointItem({ point }: { point: BulletPoint }) {
  const emphasisStyles = {
    normal: 'text-surface-600',
    highlight: 'text-primary-600 font-medium',
    critical: 'text-error font-medium',
  };

  const markdownComponents = {
    p: ({ children }: { children?: React.ReactNode }) => <span>{children}</span>,
    strong: ({ children }: { children?: React.ReactNode }) => <strong className="text-surface-800 font-semibold">{children}</strong>,
    em: ({ children }: { children?: React.ReactNode }) => <em>{children}</em>,
    code: ({ children }: { children?: React.ReactNode }) => (
      <code className="px-1 py-0.5 rounded bg-surface-100 text-primary-600 font-mono text-xs">
        {children}
      </code>
    ),
  };

  return (
    <li className="flex items-start gap-2">
      <ChevronRight className="w-4 h-4 text-surface-400 mt-0.5 flex-shrink-0" />
      <div>
        <span className={`text-sm ${emphasisStyles[point.emphasis || 'normal']}`}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {point.text}
          </ReactMarkdown>
        </span>
        {point.sub_points && point.sub_points.length > 0 && (
          <ul className="mt-1 ml-4 space-y-1">
            {point.sub_points.map((subPoint, index) => (
              <li key={index} className="text-xs text-surface-500 flex items-start gap-1">
                <span className="text-surface-400">•</span>
                <span>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {subPoint}
                  </ReactMarkdown>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

// =============================================================================
// Smart Action Bar
// =============================================================================

function SmartActionBar({
  actions,
  onAction,
}: {
  actions: SmartAction[];
  onAction?: (action: SmartAction) => void;
}) {
  const getIcon = (iconName?: string) => {
    switch (iconName) {
      case 'drill-down':
        return Search;
      case 'compare':
        return RefreshCw;
      case 'export':
        return Download;
      case 'view':
        return ExternalLink;
      default:
        return ChevronRight;
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {actions.map((action) => {
        const Icon = getIcon(action.icon);
        return (
          <button
            key={action.id}
            onClick={() => onAction?.(action)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm bg-surface-100 hover:bg-surface-200 border border-surface-200 text-surface-600 hover:text-surface-800 transition-all duration-200 cursor-pointer"
          >
            <Icon className="w-3.5 h-3.5" />
            <span>{action.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// =============================================================================
// Parse Error Display
// =============================================================================

export function ParseErrorDisplay({
  errorMessage,
  rawContent,
  details,
}: {
  errorMessage: string;
  rawContent: string;
  details?: Record<string, unknown>;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-sm bg-error-light border border-error/20 overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-error/20 flex items-center gap-2">
        <AlertCircle className="w-4 h-4 text-error" />
        <span className="text-sm font-medium text-error">
          Response Parse Error
        </span>
      </div>
      <div className="p-4 space-y-3">
        <p className="text-sm text-surface-600">{errorMessage}</p>

        {details && Object.keys(details).length > 0 && (
          <div className="text-xs">
            <div className="text-surface-500 mb-1">Details:</div>
            <pre className="bg-surface-100 rounded-sm p-2 overflow-x-auto text-surface-600">
              {JSON.stringify(details, null, 2)}
            </pre>
          </div>
        )}

        <div className="text-xs">
          <div className="text-surface-500 mb-1">Raw content (truncated):</div>
          <pre className="bg-surface-100 rounded-sm p-2 overflow-x-auto text-surface-600 max-h-32">
            {rawContent.length > 500 ? rawContent.slice(0, 500) + '...' : rawContent}
          </pre>
        </div>
      </div>
    </motion.div>
  );
}

// =============================================================================
// Text Response Display
// =============================================================================

export function TextResponseDisplay({ content, inline = false }: { content: string; inline?: boolean }) {
  const markdownContent = (
    <div className="prose prose-sm max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => (
            <p className="text-sm text-surface-600 leading-relaxed mb-3 last:mb-0">
              {children}
            </p>
          ),
          strong: ({ children }) => (
            <strong className="text-surface-800 font-semibold">{children}</strong>
          ),
          ul: ({ children }) => (
            <ul className="list-disc list-inside space-y-1 text-surface-600 mb-3">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside space-y-1 text-surface-600 mb-3">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="text-surface-600">{children}</li>,
          code: ({ children }) => (
            <code className="px-1.5 py-0.5 rounded-sm bg-surface-100 text-primary-600 font-mono text-xs">
              {children}
            </code>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );

  // When inline, render without wrapper (for use inside AgentExecutionView card)
  if (inline) {
    return markdownContent;
  }

  // Standalone use - wrap in a card
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="card rounded-sm p-4"
    >
      {markdownContent}
    </motion.div>
  );
}
